/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Table styling layer — extracted from the markup on purpose (CRCB2B-636).
 *
 * `ui/table.tsx` is the only binding that consumes this today, and a native `<table>` is
 * the default for everything. The extraction exists because **extraction is the expensive
 * half to retrofit**: left inlined in the table JSX, every class would have to be
 * rediscovered later by whoever needs a non-table structure (a whole-row link — `<a>`
 * cannot wrap `<tr>` — virtualized rows, drag-reorder, or expandable row detail needing a
 * nested region). The ARIA role mapping is mechanical by comparison, and axe's
 * `aria-required-children` / `aria-required-parent` rules catch a broken one.
 *
 * If a div binding is ever built it MUST use `display: table / table-row / table-cell`, not
 * CSS Grid. Grid cannot do it: syncing columns across rows requires the container to own
 * `grid-template-columns`, which forces rows to `display: contents` — and that removes the
 * row from the box tree, so it can paint neither background nor border and **row hover
 * dies**. Figma's own row node draws the row as a grid and pays exactly that price.
 *
 * ## Measured from Figma `Table` (`455:2481`)
 *
 * | Part | Figma | Value |
 * |---|---|---|
 * | header cell | `Table/Cell/Header` `334:2905` | 48px, `px-4`, `--muted` #f5f5f5 fill, `--muted-foreground` #585858 label |
 * | body cell | `Table/Cell/Body` `334:2069` | 64px **min**, `px-3`, 1px `--border` bottom rule |
 * | row hover | `Table/Row` `1376:9971` | `--background-color-surface-secondary` #eaeaea |
 *
 * Both cell heights are **minimums** — Figma sets `h-64` *and* `min-h-64` on the body cell,
 * and its own wrapping example runs to two lines inside one.
 *
 * **Known design inconsistency, implemented as drawn:** the header cell pads `px-4` (16px)
 * while the body cell pads `px-3` (12px), so a header label sits 4px inboard of the values
 * beneath it. Built as designed and escalated rather than silently normalised, per the
 * convention the WCAG-AA reconciliation items follow. Flagged in the style-guide story.
 */

/**
 * The scroll container. Horizontal overflow uses the **native** scrollbar per CRCB2B-636's
 * Solution Notes — no custom scrollbar.
 *
 * For a per-breakpoint choice between scrolling and wrapping, compose responsive utilities
 * on top (`className="overflow-x-auto md:overflow-x-visible"`) and opt the text column into
 * `wrap` at the matching breakpoint. Overflow is one decision spanning two elements, so it
 * is deliberately not modelled as a single variant that would silently only do half of it.
 */
export const tableContainerVariants = cva('w-full', {
    variants: {
        overflow: {
            scroll: 'overflow-x-auto',
            contain: 'overflow-x-visible',
        },
    },
    defaultVariants: {
        overflow: 'scroll',
    },
});

/**
 * `border-collapse` is what makes the cells' 1px bottom rules meet as single lines rather
 * than doubling at every boundary. `w-full` with `table-auto` keeps the browser's column
 * algorithm — the thing a div binding has to reproduce with `display: table`.
 */
export const tableVariants = cva(
    // The 1px box around the whole table is the same `--border` rule that separates rows,
    // per the Figma Examples (`8199:65593`). It sits on the `table` rather than the
    // container so `border-collapse` merges it with the cells' own bottom rules — on the
    // container it would paint a second line beside the last row's.
    'w-full caption-bottom border-collapse border border-border text-left'
);

export const tableHeadVariants = cva('');

export const tableBodyVariants = cva('');

/**
 * Header cell. The grey fill sits on the CELL, not the row, exactly as Figma models it —
 * which is also what keeps it correct when a header row is horizontally scrolled.
 */
export const tableHeaderCellVariants = cva(
    [
        'h-12 px-4 py-3 align-middle text-sm font-book tracking-[0.03em] whitespace-nowrap bg-muted text-muted-foreground',
        // A ROW header carries the same bottom rule as the body cells beside it. Without
        // this the attribute/value table's separator stops halfway across the row, under the
        // value but not the label — visible in the PDP Specifications shape
        // (`13588:36938`), where the rule must span both columns.
        '[&[scope=row]]:border-b [&[scope=row]]:border-border',
    ].join(' '),
    {
        variants: {
            align: {
                left: 'text-left',
                right: 'text-right',
            },
        },
        defaultVariants: {
            align: 'left',
        },
    }
);

/**
 * Body row. Hover is a background change only; the rules come from the cells.
 *
 * Figma's hover row additionally draws a `border-t` and a 1px top pad, which is an artifact
 * of its div structure — with `border-collapse` the neighbouring cell's bottom rule already
 * paints that line, and adding it here would double it.
 */
export const tableRowVariants = cva('transition-colors', {
    variants: {
        interactive: {
            true: 'hover:bg-muted-hover',
            false: '',
        },
    },
    defaultVariants: {
        interactive: true,
    },
});

/**
 * Body cell.
 *
 * `wrap` is the `Type` axis of Figma's `Table/Cell/Body` set: `Type=Default` (`334:2069`)
 * clips and ellipsises on one line, `Type=Text` (`12101:73186`) drops the clipping and adds
 * 8px of block padding so wrapped lines breathe. Numeric and structured values — amounts,
 * identifiers, dates — stay on `Default`; wrap is opted into per column where the full text
 * matters.
 *
 * Wrapping grows the ROW. Columns hold their positions and no stacked-card substitution
 * happens at any width — verified against the PDP Specifications component at 712px
 * (`13588:36938`) and 399px (`13869:82363`), where the same columns hold and the long value
 * simply takes more lines.
 */
export const tableCellVariants = cva(
    'min-h-16 border-b border-border px-3 text-sm font-book tracking-[0.03em] text-foreground',
    {
    variants: {
        align: {
            left: 'text-left',
            right: 'text-right',
        },
        wrap: {
            // `break-words` rather than `break-all`: a long part number should wrap at a
            // sensible point, not mid-character.
            true: 'py-2 align-middle whitespace-normal break-words',
            false: 'h-16 overflow-hidden py-0 align-middle text-ellipsis whitespace-nowrap',
        },
    },
        defaultVariants: {
            align: 'left',
            wrap: false,
        },
    }
);

export type TableCellAlign = NonNullable<VariantProps<typeof tableCellVariants>['align']>;
export type TableOverflow = NonNullable<VariantProps<typeof tableContainerVariants>['overflow']>;
